import { Box, Typography } from '@mui/material';

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import type { SpotHistoryTableColumnType } from '@/types/markets';
import { stopPropagation } from '@/utils/stopPropagation';

import { getColumnCode } from './utils';

type DetailColumn = {
  key: 'alias' | 'code' | 'currency' | 'units' | 'info';
  label: string;
  align?: 'left' | 'right';
  width?: string;
};

const DETAIL_COLUMNS: readonly DetailColumn[] = [
  { key: 'alias', label: 'Alias', width: '25%' },
  { key: 'code', label: 'Code' },
  { key: 'currency', label: 'Currency', align: 'right', width: '18%' },
  { key: 'units', label: 'Units', width: '21%' },
  { key: 'info', label: 'Info', align: 'right', width: '8%' }
];

const stopKeyboardPropagation = (event: ReactKeyboardEvent) => {
  event.stopPropagation();
};

const HeaderExpandedDetails = ({ column }: { column: SpotHistoryTableColumnType }) => {
  const code = getColumnCode(column);
  const values: Record<DetailColumn['key'], string | undefined> = {
    alias: column.alias ?? undefined,
    code,
    currency: column.currency ?? undefined,
    units: column.units ?? undefined,
    info: undefined
  };

  return (
    <Box
      component='table'
      sx={{
        borderCollapse: 'collapse',
        maxWidth: 280,
        mt: 1,
        tableLayout: 'auto',
        width: 'auto',
        mx: 1
      }}
    >
      <Box component='colgroup'>
        {DETAIL_COLUMNS.map(detailColumn => (
          <Box
            key={detailColumn.key}
            component='col'
            sx={{ width: detailColumn.width ?? 'auto' }}
          />
        ))}
      </Box>
      <Box component='thead'>
        <Box component='tr'>
          {DETAIL_COLUMNS.map(detailColumn => (
            <Typography
              key={detailColumn.key}
              component='th'
              noWrap
              scope='col'
              sx={{
                fontSize: 10,
                fontWeight: 400,
                lineHeight: 1.2,
                overflow: 'hidden',
                pl: 2,
                '&:first-of-type': { pl: 0 },
                textAlign: detailColumn.align ?? 'left',
                textOverflow: 'ellipsis'
              }}
            >
              {detailColumn.label}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box component='tbody'>
        <Box component='tr'>
          {DETAIL_COLUMNS.map(detailColumn => (
            <Typography
              key={`${code}-${detailColumn.key}`}
              component='td'
              noWrap
              sx={{
                color: 'gx.text',
                fontSize: 12,
                lineHeight: 1.3,
                overflow: 'hidden',
                pl: 2,
                '&:first-of-type': { pl: 0 },
                textAlign: detailColumn.align ?? 'left',
                textOverflow: 'ellipsis'
              }}
            >
              {detailColumn.key === 'info' ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: 18,
                    justifyContent: 'flex-end'
                  }}
                  onClick={stopPropagation}
                  onKeyDown={stopKeyboardPropagation}
                >
                  <FactsheetDialogButton
                    code={code}
                    fontSize={14}
                    showTooltip
                  />
                </Box>
              ) : (
                values[detailColumn.key] || '-'
              )}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderExpandedDetails;
